import React from 'react';
import type { SituacaoPrazo } from '../../api/dashboardOficinaApi';
import '../../shared/ui/badge-status.css';

/**
 * Dias sempre com a unidade escrita.
 *
 * Número solto numa coluna chamada "Previsão" é lido como "faltam N" — e aqui significa o prazo
 * TOTAL da etapa. A unidade não resolve isso sozinha (a hint e o cabeçalho fazem o resto), mas
 * um "5" sem nada ao lado é o pior dos casos.
 */
export const emDias = (valor: number | null | undefined): string => {
  if (valor === null || valor === undefined) return '-';
  return valor === 1 ? '1 dia' : `${valor} dias`;
};

export interface PrazoDaEtapa {
  situacaoPrazo?: SituacaoPrazo | null;
  diasNaEtapa?: number | null;
  /** O prazo TOTAL da etapa (no veículo, `estimatedDaysInStatus`). */
  previsaoDiasNaEsteira?: number | null;
}

/**
 * at-134 (ADR 0064): o semáforo do prazo. A situação vem PRONTA do backend — é a régua da aba
 * Prazos do Dashboard Oficina, e recalculá-la aqui criaria a segunda definição de prazo que o
 * ADR 0039 recusou. at-135: saiu da tela do setor de Eventos para os modais de placa.
 *
 * 🔴 `SEM_PRAZO` tem selo próprio. Sem ele, o veículo que ninguém estimou ficaria igual ao
 * veículo em dia — e em 26/10/2026, no banco de dev, eram 28 de 34 veículos.
 */
export const seloDoPrazo = (v: PrazoDaEtapa): { texto: string; classe: string } | null => {
  switch (v.situacaoPrazo) {
    case 'ESTOURADO': {
      const alem =
        v.diasNaEtapa != null && v.previsaoDiasNaEsteira != null
          ? Math.max(1, Math.round(v.diasNaEtapa - v.previsaoDiasNaEsteira))
          : null;
      return {
        texto: alem === null ? 'Atrasado' : `Atrasado há ${emDias(alem)}`,
        classe: 'badge-vermelho',
      };
    }
    case 'EM_RISCO':
      return { texto: 'Em risco', classe: 'badge-laranja' };
    case 'SEM_PRAZO':
      return { texto: 'Sem prazo definido', classe: 'badge-cinza' };
    default:
      return null;
  }
};

/** O hover: o tempo já passado na etapa, que é o par do prazo. */
export const tituloDoPrazo = (v: PrazoDaEtapa): string => {
  const etapa = v.diasNaEtapa != null ? `Há ${emDias(v.diasNaEtapa)} nesta etapa` : '';
  const previsao =
    v.previsaoDiasNaEsteira != null ? `prazo da etapa: ${emDias(v.previsaoDiasNaEsteira)}` : 'sem prazo definido';
  return etapa ? `${etapa}; ${previsao}` : previsao;
};

/**
 * O selo ao lado da placa nos modais. Dentro do prazo não desenha nada: o selo existe para chamar
 * atenção, e um "No prazo" em todo modal viraria ruído.
 */
const SeloPrazoDaEtapa: React.FC<{ car?: PrazoDaEtapa & { estimatedDaysInStatus?: number | null } | null }> = ({
  car,
}) => {
  if (!car) return null;
  const v: PrazoDaEtapa = {
    situacaoPrazo: car.situacaoPrazo,
    diasNaEtapa: car.diasNaEtapa,
    previsaoDiasNaEsteira: car.previsaoDiasNaEsteira ?? car.estimatedDaysInStatus,
  };
  const selo = seloDoPrazo(v);
  if (!selo) return null;
  return (
    <span className={`cilia-badge ${selo.classe}`} title={tituloDoPrazo(v)}>
      {selo.texto}
    </span>
  );
};

export default SeloPrazoDaEtapa;
